/**
 * CustomerLibrary
 * ───────────────
 * /konto/lizenzen — logged-in customer sees every product they own
 * (paid or admin-granted), each with a license key + a one-click
 * download button that pulls the latest version. No license key
 * typing required; the user is already authenticated.
 *
 * Reads from GET /api/me/library which dedupes by product_id and
 * mints a fresh download token per item.
 */
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, Download, Copy, CheckCircle2, Package, Gift,
  KeyRound, Loader2, AlertCircle,
} from 'lucide-react';
import FloatingHeader from '../components/layout/FloatingHeader';
import Footer from '../components/Footer';
import PageMeta from '../components/PageMeta';
import { useAetherAuth } from './Aether/AetherAuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CustomerLibrary() {
  const navigate = useNavigate();
  const location = useLocation();
  const isEnglish = location.pathname.startsWith('/en');
  const langPrefix = isEnglish ? '/en' : '';
  const { user, loading: authLoading, authFetch } = useAetherAuth();

  const [library, setLibrary] = useState(null); // { email, items: [...] }
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);

  // Wer nicht angemeldet ist, kommt zur Anmeldung. Frueher fuehrte der Weg auf
  // /redownload — dort reichte ein Lizenzschluessel ohne Konto. Seit sich jeder
  // selbst registrieren kann, ist das Konto der Weg zu den eigenen Kaeufen.
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/aether', { replace: true });
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await authFetch('/api/me/library');
        const data = await res.json();
        if (!res.ok) {
          if (!cancelled) setError(data.detail || 'Konnte Bibliothek nicht laden');
        } else if (!cancelled) {
          setLibrary(data);
        }
      } catch (e) {
        if (!cancelled) setError(String(e?.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, authFetch]);

  const copyKey = async (key) => {
    try {
      await navigator.clipboard.writeText(key);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    } catch {/* swallow */}
  };

  return (
    <motion.div
      className="min-h-screen text-white relative"
      style={{ background: 'transparent' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
    >
      <PageMeta
        title={isEnglish ? 'My library | DAVIDMEDIA' : 'Meine Lizenzen | DAVIDMEDIA'}
        description={
          isEnglish
            ? 'Your purchased products, license keys, and one-click downloads of the latest version.'
            : 'Deine gekauften Produkte, Lizenzschlüssel und Direkt-Download der aktuellen Version.'
        }
        url={`https://www.davidmedia.de${langPrefix}/konto/lizenzen`}
        noindex
      />

      <FloatingHeader />

      <main className="relative z-10 pt-32 pb-24 px-6">
        <div className="max-w-5xl mx-auto">
          <button
            onClick={() => navigate(langPrefix || '/')}
            className="flex items-center gap-2 mb-8 group"
          >
            <ArrowLeft className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-1"
                       style={{ color: 'var(--text-secondary)' }} />
            <span className="font-manrope text-sm transition-colors duration-300 group-hover:text-white"
                  style={{ color: 'var(--text-secondary)' }}>
              {isEnglish ? 'Back' : 'Zurück'}
            </span>
          </button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="mb-10">
              <h1 className="font-syne font-extrabold text-3xl md:text-4xl mb-3"
                  style={{ color: 'var(--text-primary)' }}>
                {isEnglish ? 'Your library' : 'Meine Lizenzen'}
              </h1>
              <p className="font-manrope text-base"
                 style={{ color: 'var(--text-secondary)' }}>
                {isEnglish
                  ? 'Everything you have access to. Download the latest version anytime.'
                  : 'Alles, worauf du Zugriff hast. Jederzeit die aktuellste Version herunterladen.'}
              </p>
            </div>

            {/* Loading */}
            {(authLoading || loading) && (
              <div className="flex items-center gap-3 p-6 rounded-2xl liquid-glass">
                <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--primary)' }} />
                <span className="font-mono text-sm" style={{ color: 'var(--text-secondary)' }}>
                  {isEnglish ? 'Loading…' : 'Lade …'}
                </span>
              </div>
            )}

            {/* Error */}
            {!loading && error && (
              <div className="flex items-start gap-3 p-5 rounded-2xl"
                   style={{
                     background: 'rgba(239,68,68,0.08)',
                     border: '1px solid rgba(239,68,68,0.3)',
                   }}>
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" style={{ color: '#ef4444' }} />
                <div className="text-sm" style={{ color: '#fca5a5' }}>{error}</div>
              </div>
            )}

            {/* Empty */}
            {!loading && !error && library?.items?.length === 0 && (
              <div className="text-center py-16 rounded-2xl liquid-glass">
                <Package className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
                <p className="font-syne font-bold text-xl mb-2" style={{ color: 'var(--text-primary)' }}>
                  {isEnglish ? 'No products yet' : 'Noch keine Produkte'}
                </p>
                <p className="font-manrope text-sm mb-6 max-w-md mx-auto"
                   style={{ color: 'var(--text-secondary)' }}>
                  {isEnglish
                    ? 'Once you buy or receive a license, it shows up here.'
                    : 'Sobald du etwas kaufst oder eine Lizenz bekommst, taucht sie hier auf.'}
                </p>
                <a href={`${langPrefix}/shop`}
                   className="inline-block px-6 py-3 rounded-full font-mono text-[11px] uppercase tracking-widest text-black transition-transform hover:scale-105"
                   style={{ background: 'var(--primary)' }}>
                  {isEnglish ? 'Browse Shop' : 'Shop ansehen'}
                </a>
              </div>
            )}

            {/* Items grid */}
            {!loading && !error && library?.items?.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5"
                   data-testid="customer-library-grid">
                {library.items.map((item) => (
                  <div key={item.product_id}
                       className="p-6 rounded-2xl liquid-glass flex flex-col"
                       data-testid={`library-card-${item.product_id}`}>
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] mb-1"
                           style={{ color: 'var(--text-muted)' }}>
                          {item.granted
                            ? (isEnglish ? 'Granted' : 'Freigeschaltet')
                            : (isEnglish ? 'Purchased' : 'Gekauft')}
                          {' · '}
                          {item.license_type === 'team'
                            ? (isEnglish ? 'Team' : 'Team')
                            : (isEnglish ? 'Single' : 'Single')}
                        </p>
                        <h2 className="font-syne font-bold text-xl"
                            style={{ color: 'var(--text-primary)' }}>
                          {item.product_name}
                        </h2>
                        <p className="font-mono text-xs mt-1"
                           style={{ color: 'var(--text-secondary)' }}>
                          v{item.version}
                        </p>
                      </div>
                      {item.granted && (
                        <Gift className="w-5 h-5 shrink-0" style={{ color: 'var(--primary)' }} />
                      )}
                    </div>

                    {/* License key */}
                    {item.license_key && (
                      <div className="mb-4">
                        <p className="font-mono text-[9px] uppercase tracking-[0.25em] mb-2"
                           style={{ color: 'var(--text-muted)' }}>
                          {isEnglish ? 'License key' : 'Lizenzschlüssel'}
                        </p>
                        <div className="flex items-center gap-2 p-3 rounded-xl"
                             style={{
                               background: 'rgba(255,255,255,0.04)',
                               border: '1px solid var(--glass-border)',
                             }}>
                          <KeyRound className="w-4 h-4 shrink-0" style={{ color: 'var(--primary)' }} />
                          <code className="flex-1 font-mono text-xs tracking-wider truncate"
                                style={{ color: 'var(--text-primary)' }}>
                            {item.license_key}
                          </code>
                          <button onClick={() => copyKey(item.license_key)}
                                  className="p-1.5 rounded transition hover:bg-white/10"
                                  title={isEnglish ? 'Copy' : 'Kopieren'}>
                            {copiedKey === item.license_key
                              ? <CheckCircle2 className="w-4 h-4" style={{ color: '#22c55e' }} />
                              : <Copy className="w-4 h-4" style={{ color: 'var(--text-secondary)' }} />}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Download */}
                    {item.download_url ? (
                      <a href={item.download_url}
                         className="mt-auto w-full flex items-center justify-center gap-2 rounded-full py-3 font-mono text-[11px] uppercase tracking-widest text-black transition-all hover:scale-[1.01]"
                         style={{
                           background: 'var(--primary)',
                           boxShadow: '0 0 24px rgba(249,115,22,0.3)',
                         }}
                         data-testid={`library-download-${item.product_id}`}>
                        <Download className="w-4 h-4" />
                        {isEnglish ? 'Download v' : 'Download v'}{item.version}
                      </a>
                    ) : (
                      <div className="mt-auto w-full py-3 rounded-full text-center font-mono text-[11px] uppercase tracking-widest"
                           style={{
                             background: 'rgba(255,255,255,0.04)',
                             color: 'var(--text-muted)',
                           }}>
                        {isEnglish ? 'No download yet' : 'Noch kein Download'}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </main>

      <Footer />
    </motion.div>
  );
}
